import { Conversation, MembersData, MessagesData, Server } from '@nextforce/api'
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { useConversations } from 'chat/conversations/queries/list'
import { isRepository } from 'chat/conversations/utils'
import { fetchMembers } from 'chat/members/queries/list'
import { useNonDeletedMessages } from 'chat/messages/queries/list'
import moment from 'moment'
import { useCallback } from 'react'
import { getPrimaryRole } from 'shared/utils'
import { useUser } from 'users/queries/detail'
import keys from '../../shared/queries/keys'
import { convertMemberToComposeUser } from './shared/convert'
import { ComposeGroup, ComposeUser } from './shared/types'

export function useComposeMessageRecipients(enabled = true) {
  const { server } = useServer()
  const client = useQueryClient()

  const { data: user } = useUser()
  const { data: conversations } = useConversations()

  const filterUserMessages = useCallback(
    (messagesData: MessagesData) =>
      messagesData.messages.filter(m => m.user.id === user?.id) ?? [],
    [user?.id]
  )

  const { data: userMessages } = useNonDeletedMessages(filterUserMessages)

  const storeAllGroup = conversations?.filter(
    conversation => conversation.type === 'storeAll'
  )

  const companyAnnouncementsGroup = conversations?.find(
    conversation => conversation.type === 'companyAnnouncements'
  )

  const nonDMConversationsWithAllowedToPost = conversations?.filter(
    conversation =>
      conversation.postsAllowed !== 'none' &&
      conversation.type !== 'dm' &&
      !isRepository(conversation)
  )

  const query = useQuery({
    queryKey: keys.composeRecipients,
    queryFn: fetchRecipients,
    enabled,
  })

  const oldestMessageTimeStamp =
    userMessages &&
    userMessages.length > 0 &&
    userMessages[userMessages.length - 1].createdAt

  if (
    query.data &&
    oldestMessageTimeStamp &&
    moment(Date.now()).diff(oldestMessageTimeStamp, 'days') > 7 * 3
  ) {
    query.data.sort((a, b) => {
      if (a.type === 'user' && b.type === 'user') {
        const aRole = getPrimaryRole(a.roles) ?? ''
        const bRole = getPrimaryRole(b.roles) ?? ''
        return bRole.localeCompare(aRole)
      } else if (a.type === 'user' && b.type === 'group') {
        return -1
      } else if (a.type === 'group' && b.type === 'user') {
        return 1
      }
      return 0
    })
  }

  return query

  async function fetchMemberRecipients(): Promise<ComposeUser[]> {
    const combinedMembersResponse: ComposeUser[] = []

    const fetchStoreGroupMembers = async (
      conversation: Conversation
    ): Promise<ComposeUser[]> => {
      const storeGroupMembers = await fetchComposeUsersByConversation(
        server,
        client,
        conversation
      )
      combinedMembersResponse.push(...storeGroupMembers)
      return storeGroupMembers
    }

    const storeMembersPromise = async () =>
      storeAllGroup?.map(fetchStoreGroupMembers) ?? []

    const otherMembersPromise = async () => {
      if (companyAnnouncementsGroup) {
        const otherMembers = await fetchComposeUsersByConversation(
          server,
          client,
          companyAnnouncementsGroup,
          true
        )
        combinedMembersResponse.push(...otherMembers)
        return otherMembers
      }
    }

    await Promise.all([storeMembersPromise(), otherMembersPromise()])

    return combinedMembersResponse
  }

  async function fetchRecipients() {
    const combinedMembersResponse: ComposeUser[] = await fetchMemberRecipients()
    const uniqueComposeMemberRecipients = combinedMembersResponse.filter(
      (member, index) =>
        index === combinedMembersResponse.findIndex(m => m.id === member.id)
    )
    const composeGroupRecipients: ComposeGroup[] =
      nonDMConversationsWithAllowedToPost?.map(conversation => {
        return { id: conversation.id, title: conversation.name, type: 'group' }
      }) ?? []
    return [...uniqueComposeMemberRecipients, ...composeGroupRecipients]
  }
}

async function fetchComposeUsersByConversation(
  server: Server,
  client: QueryClient,
  conversation: Conversation,
  fetchStaffAndGuestUsers: boolean = false
) {
  let groupMembers: ComposeUser[] = []
  const membersCache = client.getQueryData<MembersData>(
    keys.members(conversation.id, [])
  )
  if (!fetchStaffAndGuestUsers && membersCache) {
    groupMembers = membersCache.map(member =>
      convertMemberToComposeUser(member)
    )
  } else {
    const membersData = await fetchMembers({
      conversationId: conversation.id,
      server,
      fetchStaffAndGuestUsers,
    })
    client.setQueryData<MembersData>(
      keys.members(conversation.id, []),
      membersData
    )
    groupMembers = membersData.map(member => convertMemberToComposeUser(member))
  }
  return groupMembers
}
